// Device-flow activation screen (RFC 8628, Agent Platform Phase 8). The user
// arrives from /activate with (or types) the short code their device showed,
// reviews the app + requested scopes, and approves or denies. The backend
// enforces same-tenant, expiry and the single pending→decided transition —
// this screen is presentation + the explicit user grant.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, MonitorSmartphone, ShieldAlert, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  decideDeviceCode, getScopeCatalog, lookupDeviceCode,
  type DeviceLookup, type ScopeCatalogEntry,
} from '@/services/developerService';
import { getApiErrorMessage } from '@/lib/apiError';

const normalize = (v: string) => v.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9);

export const DeviceActivateConsentPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [params] = useSearchParams();

  const [code, setCode] = useState(normalize(params.get('user_code') || ''));
  const [lookup, setLookup] = useState<DeviceLookup | null>(null);
  const [looking, setLooking] = useState(false);
  const [decided, setDecided] = useState<'approved' | 'denied' | null>(null);
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);
  const [descriptions, setDescriptions] = useState<Record<string, ScopeCatalogEntry>>({});

  useEffect(() => {
    let alive = true;
    getScopeCatalog()
      .then((cats) => {
        if (!alive) return;
        const map: Record<string, ScopeCatalogEntry> = {};
        cats.forEach((c) => c.scopes.forEach((s) => { map[s.scope] = s; }));
        setDescriptions(map);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const find = async (value: string) => {
    if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(value)) {
      toast.error(t('oauth.device_code_format', { defaultValue: 'Enter the code exactly as shown, e.g. ABCD-2345.' }));
      return;
    }
    setLooking(true);
    try {
      setLookup(await lookupDeviceCode(value));
    } catch (e: any) {
      setLookup(null);
      toast.error(getApiErrorMessage(e, t('oauth.device_not_found', { defaultValue: 'That code was not found (or it expired).' })));
    } finally {
      setLooking(false);
    }
  };

  // A code arriving in the URL is looked up immediately.
  useEffect(() => {
    if (code && /^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code)) void find(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (approve: boolean) => {
    if (!lookup) return;
    setSubmitting(approve ? 'allow' : 'deny');
    try {
      await decideDeviceCode(lookup.userCode, approve);
      setDecided(approve ? 'approved' : 'denied');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('oauth.device_decide_failed', { defaultValue: 'Could not record your decision.' })));
    } finally {
      setSubmitting(null);
    }
  };

  const scopeRows = useMemo(
    () => (lookup?.scopes || []).map((s) => ({ scope: s, description: descriptions[s]?.description })),
    [lookup, descriptions],
  );

  return (
    <div className="max-w-md mx-auto mt-10">
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
              <MonitorSmartphone className="w-6 h-6 text-foreground" />
            </div>
            <h1 className="text-h3 text-charcoal">
              {t('oauth.device_title', { defaultValue: 'Connect a device' })}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('oauth.device_subtitle', { defaultValue: 'Enter the code shown on your device or terminal.' })}
            </p>
          </div>

          {decided ? (
            <div className="text-center space-y-2 py-4">
              {decided === 'approved' ? (
                <Check className="w-8 h-8 text-green-600 mx-auto" />
              ) : (
                <X className="w-8 h-8 text-muted-foreground mx-auto" />
              )}
              <p className="text-foreground font-medium">
                {decided === 'approved'
                  ? t('oauth.device_approved', { defaultValue: 'Device connected — you can return to it now.' })
                  : t('oauth.device_denied', { defaultValue: 'Request denied. The device will not get access.' })}
              </p>
            </div>
          ) : !lookup ? (
            <div className="space-y-3">
              <input
                value={code}
                onChange={(e) => setCode(normalize(e.target.value))}
                placeholder="ABCD-2345"
                autoFocus
                className="w-full text-center text-2xl tracking-[0.3em] font-mono rounded-md border border-border bg-background py-3 uppercase"
              />
              <Button className="w-full" disabled={looking} onClick={() => find(code)}>
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : t('oauth.device_continue', { defaultValue: 'Continue' })}
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-border p-3">
                <p className="text-sm text-muted-foreground">
                  {t('oauth.device_asking', { defaultValue: 'This is asking for access to your account:' })}
                </p>
                <p className="text-foreground font-medium mt-1">{lookup.application.name}</p>
                <code className="text-xs text-muted-foreground">{lookup.application.clientId}</code>
              </div>
              <ul className="space-y-1.5">
                {scopeRows.map((r) => (
                  <li key={r.scope} className="text-sm flex items-start gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    <span>
                      <code className="text-xs">{r.scope}</code>
                      {r.description ? <span className="text-muted-foreground"> — {r.description}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                {t('oauth.device_acts_as_you', {
                  defaultValue: 'Approving lets the device act as you, within these scopes, until its session expires.',
                })}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={!!submitting} onClick={() => decide(false)}>
                  {submitting === 'deny' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('oauth.deny', { defaultValue: 'Deny' })}
                </Button>
                <Button className="flex-1" disabled={!!submitting} onClick={() => decide(true)}>
                  {submitting === 'allow' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('oauth.allow', { defaultValue: 'Approve' })}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeviceActivateConsentPage;
